'use client';

import {Box, styled, Typography} from '@mui/material';
import {StyledInformationLinkButton} from '@/components/sections/information-link-button-section/InformationLinkButtonSection.styles';

// Hero CTA button — same look as the shared information-link button but with the
// more generous padding shown in the design.
export const StyledHeroV2CtaButton = styled(StyledInformationLinkButton)(({theme}) => ({
  padding: theme.spacing(2, 4.5),
  fontSize: '1.125rem',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.25, 3),
    fontSize: '1rem',
  },
}));

// Full-width dark-red hero band (auto height, unlike the legacy 100vh hero).
// The background is split by a hard diagonal line: the left two thirds use the
// lighter red, the right third the darker red (per design).
export const StyledHeroV2Section = styled('section')(({theme}) => ({
  position: 'relative',
  width: '100%',
  color: theme.palette.common.white,
  backgroundColor: theme.palette.tv4Red.red04,
  paddingTop: theme.spacing(16),
  // No bottom padding: the red band's bottom edge is pulled up to the cards'
  // vertical centre by the negative margin on the cards row below, so the white
  // page background shows behind the lower half of the cards (per design).
  paddingBottom: 0,
  // The cards overflow ~95px below the section box (see the negative margin on
  // the cards row); this margin must clear that overflow plus leave breathing
  // room before the next section.
  marginBottom: theme.spacing(20),
  // Hard diagonal split overlaid on the lighter base red. The boundary runs from
  // the horizontal centre at the bottom (50%) up to ~92% at the top, so the
  // darker red is a narrow sliver at the top and widens to the right half at the
  // bottom — still a straight diagonal, regardless of the band's height.
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundColor: theme.palette.tv4Red.red05,
    clipPath: 'polygon(50% 100%, 92% 0, 100% 0, 100% 100%)',
    zIndex: 0,
    pointerEvents: 'none',
  },
  // Keep the section content above the diagonal overlay.
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(10),
    // No straddle on mobile, so the large desktop bottom margin isn't needed.
    marginBottom: theme.spacing(6),
  },
}));

// Two-column top row: text on the left, contained media on the right.
export const StyledHeroV2TopRow = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(6),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(4),
  },
}));

export const StyledHeroV2TextColumn = styled(Box)(({theme}) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
}));

export const StyledHeroV2Eyebrow = styled(Typography)(({theme}) => ({
  color: theme.palette.common.white,
}));

export const StyledHeroV2MediaBox = styled(Box)(({theme}) => ({
  position: 'relative',
  flex: 1,
  width: '100%',
  aspectRatio: '4 / 3',
  borderRadius: theme.shape.borderRadiusLg,
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    aspectRatio: '16 / 9',
  },
}));

// Cards row — keeps the same dark-card look across breakpoints.
// The negative bottom margin (half the 190px card height) pulls the section's
// red bottom edge up to the cards' vertical centre, so the lower half of each
// card overlaps onto the white page background below.
export const StyledHeroV2CardsRow = styled(Box)(({theme}) => ({
  position: 'relative',
  zIndex: 1,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: theme.spacing(3),
  // Push the cards further down so more red surface is left for the hero
  // content above; the 50/50 straddle is preserved by the negative margin below.
  marginTop: theme.spacing(12),
  marginBottom: -95,
  // Mobile: the cards stack on a full-width white shelf below the red hero
  // (no straddle). The negative horizontal margins (equal to the Container's
  // 16px gutter) bleed the shelf out to the viewport edges, and the matching
  // horizontal padding re-insets the cards so they align with the text above.
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    marginBottom: 0,
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
}));
